/**
 * SEOLink Component
 * 
 * A wrapper around React Router's Link that enforces canonical URL rules:
 * - Removes trailing slashes (except homepage)
 * - Validates internal paths
 * - Logs warnings in development for non-canonical URLs
 * 
 * Usage:
 * import { SEOLink } from '../components/seo/SEOLink';
 * <SEOLink to="/services">Services</SEOLink>
 * 
 * Output: <Link to="/services">Services</Link>
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { normalizeUrl, shouldBlockLink } from '../../utils/seoLinkNormalizer';

/**
 * SEOLink - Normalized internal link component
 */
export const SEOLink = ({ to, children, className, onClick, ...props }) => {
  // Normalize the URL
  const normalizedTo = normalizeUrl(to);
  
  // Warn in development if URL was blocked
  if (process.env.NODE_ENV === 'development' && shouldBlockLink(to)) {
    console.warn(`[SEO Warning] Blocked pattern in link: ${to}`);
  }
  
  // Warn if normalization changed the URL
  if (process.env.NODE_ENV === 'development' && to !== normalizedTo && to) {
    console.info(`[SEO] Link normalized: "${to}" → "${normalizedTo}"`);
  }
  
  return (
    <Link to={normalizedTo} className={className} onClick={onClick} {...props}>
      {children}
    </Link>
  );
};

/**
 * SEONavLink - For navigation with active state detection
 * (Preserves additional props for styling active states)
 */
export const SEONavLink = ({ to, children, className, activeClassName, ...props }) => {
  const normalizedTo = normalizeUrl(to);
  
  return (
    <Link to={normalizedTo} className={className} {...props}>
      {children}
    </Link>
  );
};

/**
 * Hook to get normalized URL
 */
export const useNormalizedUrl = (url) => {
  return normalizeUrl(url);
};

export default SEOLink;
